import { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Button, Form, Modal, Badge, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencilAlt, faTimes, faCheck, faKey, faCalendar, faEnvelope, faShieldAlt } from '@fortawesome/free-solid-svg-icons';
import styles from './UserProfile.module.css';
import API from '../api';
import PasswordResetModal from './profile/PasswordResetModal';

const availableLanguages = {
    HTML: '#e34c26', Java: '#b07219', Python: '#3572A5', PHP: '#4F5D95',
    JavaScript: '#f1e05a', TypeScript: '#2b7489', C: '#555555', 'C++': '#f34b7d',
    'C#': '#178600', Ruby: '#701516', Go: '#00ADD8', Swift: '#ffac45',
    Kotlin: '#F18E33', Rust: '#dea584', Scala: '#c22d40', Dart: '#00B4AB',
    Lua: '#000080', Haskell: '#5e5086', Perl: '#0298c3', R: '#198CE7',
    Shell: '#89e051', PowerShell: '#012456', Groovy: '#e69f56', Elixir: '#6e4a7e',
    Clojure: '#db5855', Julia: '#a270ba', Erlang: '#B83998', 'Objective-C': '#438eff',
    Assembly: '#6E4C13', MATLAB: '#e16737', Fortran: '#4d41b1', COBOL: '#004100',
    Lisp: '#3fb68b', 'F#': '#b845fc', OCaml: '#3be133', Prolog: '#74283c',
    Scheme: '#1e4aec', VHDL: '#adb2cb', Verilog: '#b2b7f8', SQL: '#e38c00',
    Apex: '#1797c0',
};

const availableOperatingSystems = [
    'Windows', 'macOS', 'Linux', 'Ubuntu', 'Fedora', 'Debian',
    'Kali Linux', 'Arch Linux', 'Red Hat', 'Solaris', 'FreeBSD', 'Raspberry Pi OS',
];

function UserProfile() {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [saveError, setSaveError] = useState(null);

    // Edit states — per-section
    const [editingSection, setEditingSection] = useState(null); // 'identity' | 'about' | null
    const [formData, setFormData] = useState({ profile_picture: '', description: '' });
    const [selectedLanguages, setSelectedLanguages] = useState([]);
    const [selectedOS, setSelectedOS] = useState([]);

    // Modals
    const [showLanguageModal, setShowLanguageModal] = useState(false);
    const [showOSModal, setShowOSModal] = useState(false);
    const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);

    useEffect(() => {
        fetch(API.USER_PROFILE, { method: 'GET', credentials: 'include' })
        .then(response => { if (!response.ok) throw new Error('Network response was not ok'); return response.json(); })
        .then(data => {
            if (data.status === 'error') { setLoadError(data.message); }
            else {
                setUser(data);
                resetFormData(data);
            }
            setLoading(false);
        })
        .catch(err => { setLoadError(err.message); setLoading(false); });
    }, []);

    const resetFormData = (data) => {
        setFormData({
            profile_picture: data.profile_picture || '',
            description: data.description || '',
        });
        setSelectedLanguages((data.language_proficiency || '').split(',').filter(Boolean));
        setSelectedOS((data.os_preference || '').split(',').filter(Boolean));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const toggleLanguage = (language) => {
        setSelectedLanguages(prev => {
            if (prev.includes(language)) return prev.filter(l => l !== language);
            if (prev.length < 10) return [...prev, language];
            return prev;
        });
    };

    const toggleOS = (os) => {
        setSelectedOS(prev => {
            if (prev.includes(os)) return prev.filter(o => o !== os);
            if (prev.length < 5) return [...prev, os];
            return prev;
        });
    };

    const handleSave = () => {
        const updatedFormData = {
            ...formData,
            language_proficiency: selectedLanguages.join(','),
            os_preference: selectedOS.join(','),
        };
        fetch(API.USER_EDIT_PROFILE, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(updatedFormData),
        })
        .then(response => response.json())
        .then(data => {
            if (data.status === 'success') {
                setUser(data.user);
                setEditingSection(null);
                setSaveError(null);
            } else {
                setSaveError(data.message || 'Failed to save changes');
            }
        })
        .catch(err => setSaveError(err.message || 'Failed to save changes'));
    };

    const handleCancel = () => {
        resetFormData(user);
        setEditingSection(null);
        setSaveError(null);
    };

    if (loadError) return <p className={styles.errorMessage}>Error: {loadError}</p>;
    if (loading) return <div className="loading-spinner"></div>;

    const isEditing = (section) => editingSection === section;

    return (
        <Container fluid className={styles.profileContainer}>
            {saveError && (
                <Alert
                    variant="danger"
                    dismissible
                    onClose={() => setSaveError(null)}
                >
                    {saveError}
                </Alert>
            )}

            {/* ── Profile Header Card ── */}
            <Card className={styles.headerCard}>
                <Card.Body className={styles.headerBody}>
                    <div className={styles.headerContent}>
                        <div className={styles.avatarSection}>
                            <img
                                className={styles.avatar}
                                src={isEditing('identity') ? (formData.profile_picture || '/default-profile.svg') : (user.profile_picture || '/default-profile.svg')}
                                alt={user.username}
                            />
                            {isEditing('identity') && (
                                <>
                                    <Form.Control
                                        type="text"
                                        name="profile_picture"
                                        value={formData.profile_picture}
                                        onChange={handleInputChange}
                                        placeholder="Paste image URL"
                                        className={styles.avatarInput}
                                        size="sm"
                                    />
                                    <small className={styles.avatarHint}>Enter a URL to change your picture</small>
                                </>
                            )}
                        </div>
                        <div className={styles.headerInfo}>
                            <h2 className={styles.username}>{user.username}</h2>
                            <Badge className={styles.rankBadge} bg={user.rank === 'root' ? 'danger' : user.rank === 'member' ? 'primary' : 'secondary'}>
                                <FontAwesomeIcon icon={faShieldAlt} /> {user.rank}
                            </Badge>
                            <div className={styles.headerMeta}>
                                <span><FontAwesomeIcon icon={faEnvelope} /> {user.email}</span>
                                <span><FontAwesomeIcon icon={faCalendar} /> Joined {new Date(user.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
                            </div>
                        </div>
                        <div className={styles.headerActions}>
                            {isEditing('identity') ? (
                                <>
                                    <Button size="sm" variant="success" onClick={handleSave}><FontAwesomeIcon icon={faCheck} /> Save</Button>
                                    <Button size="sm" variant="outline-secondary" onClick={handleCancel}><FontAwesomeIcon icon={faTimes} /> Cancel</Button>
                                </>
                            ) : (
                                <Button size="sm" variant="outline-primary" onClick={() => setEditingSection('identity')}>
                                    <FontAwesomeIcon icon={faPencilAlt} /> Edit
                                </Button>
                            )}
                        </div>
                    </div>
                </Card.Body>
            </Card>

            <Row className={styles.detailRow}>
                {/* ── About Card ── */}
                <Col lg={8}>
                    <Card className={styles.detailCard}>
                        <Card.Header className={styles.cardHeader}>
                            <span>About</span>
                            {!isEditing('about') && (
                                <Button size="sm" variant="link" className={styles.editBtn} onClick={() => setEditingSection('about')}>
                                    <FontAwesomeIcon icon={faPencilAlt} />
                                </Button>
                            )}
                        </Card.Header>
                        <Card.Body>
                            {isEditing('about') ? (
                                <>
                                    <Form.Control
                                        as="textarea"
                                        rows={4}
                                        name="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        placeholder="Tell us about yourself..."
                                        className={styles.descriptionInput}
                                    />
                                    <div className={styles.editActions}>
                                        <Button size="sm" variant="success" onClick={handleSave}><FontAwesomeIcon icon={faCheck} /> Save</Button>
                                        <Button size="sm" variant="outline-secondary" onClick={handleCancel}><FontAwesomeIcon icon={faTimes} /> Cancel</Button>
                                    </div>
                                </>
                            ) : (
                                <p className={styles.description}>{user.description || 'No description provided.'}</p>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                {/* ── Actions Card ── */}
                <Col lg={4}>
                    <Card className={styles.detailCard}>
                        <Card.Header className={styles.cardHeader}>Account</Card.Header>
                        <Card.Body className={styles.actionsBody}>
                            <Button variant="warning" className={styles.actionBtn} onClick={() => setShowPasswordResetModal(true)}>
                                <FontAwesomeIcon icon={faKey} /> Change Password
                            </Button>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row className={styles.detailRow}>
                {/* ── Languages Card ── */}
                <Col lg={6}>
                    <Card className={styles.detailCard}>
                        <Card.Header className={styles.cardHeader}>
                            <span>Languages</span>
                            <Button size="sm" variant="link" className={styles.editBtn} onClick={() => setShowLanguageModal(true)}>
                                <FontAwesomeIcon icon={faPencilAlt} />
                            </Button>
                        </Card.Header>
                        <Card.Body>
                            <div className={styles.tagsContainer}>
                                {selectedLanguages.length > 0 ? selectedLanguages.map(lang => (
                                    <span key={lang} className={styles.tag} style={{ backgroundColor: availableLanguages[lang] }}>{lang}</span>
                                )) : (
                                    <span className={styles.emptyState}>No languages selected</span>
                                )}
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                {/* ── OS Preferences Card ── */}
                <Col lg={6}>
                    <Card className={styles.detailCard}>
                        <Card.Header className={styles.cardHeader}>
                            <span>Operating Systems</span>
                            <Button size="sm" variant="link" className={styles.editBtn} onClick={() => setShowOSModal(true)}>
                                <FontAwesomeIcon icon={faPencilAlt} />
                            </Button>
                        </Card.Header>
                        <Card.Body>
                            <div className={styles.tagsContainer}>
                                {selectedOS.length > 0 ? selectedOS.map(os => (
                                    <span key={os} className={styles.tag}>{os}</span>
                                )) : (
                                    <span className={styles.emptyState}>No OS preferences selected</span>
                                )}
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* ── Language Selection Modal (Bootstrap) ── */}
            <Modal show={showLanguageModal} onHide={() => { setShowLanguageModal(false); handleSave(); }} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Select Languages <small className="text-muted">(Max 10)</small></Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className={styles.modalTags}>
                        {Object.keys(availableLanguages).map(language => (
                            <div
                                key={language}
                                className={`${styles.modalTag} ${selectedLanguages.includes(language) ? styles.modalTagSelected : ''}`}
                                style={{ backgroundColor: availableLanguages[language] }}
                                onClick={() => toggleLanguage(language)}
                            >
                                {language} {selectedLanguages.includes(language) && '✓'}
                            </div>
                        ))}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <span className="text-muted">{selectedLanguages.length}/10 selected</span>
                    <Button variant="primary" onClick={() => { setShowLanguageModal(false); handleSave(); }}>Done</Button>
                </Modal.Footer>
            </Modal>

            {/* ── OS Selection Modal (Bootstrap) ── */}
            <Modal show={showOSModal} onHide={() => { setShowOSModal(false); handleSave(); }} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Select Operating Systems <small className="text-muted">(Max 5)</small></Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className={styles.modalTags}>
                        {availableOperatingSystems.map(os => (
                            <div
                                key={os}
                                className={`${styles.modalTag} ${selectedOS.includes(os) ? styles.modalTagSelected : ''}`}
                                onClick={() => toggleOS(os)}
                            >
                                {os} {selectedOS.includes(os) && '✓'}
                            </div>
                        ))}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <span className="text-muted">{selectedOS.length}/5 selected</span>
                    <Button variant="primary" onClick={() => { setShowOSModal(false); handleSave(); }}>Done</Button>
                </Modal.Footer>
            </Modal>

            {/* ── Password Reset Modal ── */}
            <PasswordResetModal show={showPasswordResetModal} onHide={() => setShowPasswordResetModal(false)} styles={styles} />
        </Container>
    );
}

export default UserProfile;
